import { Box, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material';
import {
  DatePicker,
  AdvancedFieldWrapperVertical as FieldWrapperVertical,
  ReadOnly,
  useEntityPermission,
  useFormPermissions,
  useLocalization,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { forwardRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  MAX_WIDTH_HORZ_LAYOUT,
  NON_RECURRING_DURATION,
} from '../../../../utils/constants';
import EndDate from '../EndDate';
import TimeSlotsAvaibilitySelector from '../../../TimeSlotsAvaibilitySelector';

const NonRecurringDurationAndOptions = forwardRef((props, ref) => {
  const { control, errors } = useFormContext();
  const { formatMessage } = useLocalization();
  const { isReadOnly } = useEntityPermission();
  const value = useWatch({ control, name: props?.name });
  const { permissions } = useFormPermissions();

  const singleDay = value === NON_RECURRING_DURATION(formatMessage)[0].value;

  let readOnly = false;

  if (isReadOnly(permissions[props?.name], value)) {
    readOnly = true;
  }

  return (
    <Stack spacing={2}>
      <FieldWrapperVertical
        horizontal
        labelMaxWidth={MAX_WIDTH_HORZ_LAYOUT}
        field={{
          name: props.name,
          label: {
            localeId: 'DURATION',
            defaultMessage: 'Duration',
          },
          required: true,
        }}
        errors={errors}>
        {readOnly ? (
          <ReadOnly name={props?.name}>
            <FormattedMessage
              id={value}
              defaultMessage={
                NON_RECURRING_DURATION(formatMessage).find(
                  d => d.value === value,
                ).label
              }
            />
          </ReadOnly>
        ) : (
          <Controller
            control={control}
            name={props.name}
            render={({ field }) => (
              <RadioGroup ref={ref} row {...field}>
                {NON_RECURRING_DURATION(formatMessage).map(item => {
                  return (
                    <FormControlLabel
                      key={item.label}
                      value={item.value}
                      control={<Radio size="small" />}
                      label={item?.label}
                    />
                  );
                })}
              </RadioGroup>
            )}
          />
        )}
      </FieldWrapperVertical>
      {singleDay && (
        <FieldWrapperVertical
          horizontal
          labelMaxWidth={MAX_WIDTH_HORZ_LAYOUT}
          field={{
            name: 'startDate',
            label: {
              localeId: 'DATE',
              defaultMessage: 'Date',
            },

            required: true,
          }}
          errors={errors}>
          <DatePicker
            boxProps={{ sx: { maxWidth: '300px' } }}
            disablePast
            permission={permissions.startDate}
            name="startDate"
          />
        </FieldWrapperVertical>
      )}
      {!singleDay && (
        <FieldWrapperVertical
          horizontal
          labelMaxWidth={MAX_WIDTH_HORZ_LAYOUT}
          field={{
            name: 'dateRange',
            label: {
              localeId: 'DATE_RANGE',
              defaultMessage: 'Date Range',
            },
            required: true,
          }}
          errors={errors}>
          {!readOnly && (
            <Stack direction="row" spacing={2}>
              <FieldWrapperVertical
                field={{
                  name: 'startDate',
                  label: {
                    localeId: 'START_DATE',
                    defaultMessage: 'Start Date',
                  },
                }}
                errors={errors}>
                <DatePicker
                  disablePast
                  permission={permissions.startDate}
                  name="startDate"
                />
              </FieldWrapperVertical>
              <FieldWrapperVertical
                field={{
                  name: 'endDate',
                  label: {
                    localeId: 'END_DATE',
                    defaultMessage: 'End Date',
                  },
                }}
                errors={errors}>
                <EndDate permission={permissions.endDate} />
              </FieldWrapperVertical>
            </Stack>
          )}
          {readOnly && (
            <Box>
              <ReadOnly>
                <DatePicker
                  permission={permissions.startDate}
                  name="startDate"
                />{' '}
                <FormattedMessage id="TO_SMALL" defaultMessage="to" />{' '}
                <EndDate permission={permissions.endDate} />
              </ReadOnly>
            </Box>
          )}
        </FieldWrapperVertical>
      )}
      <FieldWrapperVertical
        horizontal
        labelMaxWidth={MAX_WIDTH_HORZ_LAYOUT}
        field={{
          name: 'slots',
          label: {
            localeId: 'TIME_SLOTS',
            defaultMessage: 'Time Slots',
          },
          required: true,
        }}
        errors={errors}>
        <TimeSlotsAvaibilitySelector
          permission={permissions.slotAvailability}
        />
      </FieldWrapperVertical>
    </Stack>
  );
});

export default NonRecurringDurationAndOptions;
